import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';

import { HeaderLink, HeaderDrawer }from './index.js';
import { HeaderLogo, IconChevronSprite } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';
import MobileNavigation from '$partials/global/mobile-navigation.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const Header = ({
    bgStyle,
    isOpen,
    menu,
    setIsOpen
}) => {
    // misc
    let buttonClass;
    let textColorClass;
    switch(bgStyle) {
        case 'transparent':
            buttonClass = 'button--primary';
            textColorClass = 'text-white';
            break;

        case 'transparent-landing':
            buttonClass = 'button--primary';
            textColorClass = 'text-white';
            break;

        case 'white':
            buttonClass = 'button--secondary-alt';
            textColorClass = 'text-black';
            break;

        default:
            buttonClass = 'button--primary-alt';
            textColorClass = 'text-black';
    }

    // hooks
    const {
        formatURI,
        translate: t,
    } = useLocale();

    useEffect(() => {
        if (isOpen === false) {
            clearAllBodyScrollLocks();
        }
    }, [ isOpen ]);

    // misc
    if (!menu) { return null; }

    // Format links helper function
    const createMenuItem = (item, title, index) => {
        const collection = item?.dropdown?.length > 0 ? item?.dropdown : item?.single;
        const dropdown = collection?.length > 0;

        return {
            collection,
            dropdown,
            index,
            title
        };
    };

    const menuItems = [
        createMenuItem({ dropdown: menu?.link1DropdownCollection?.items, single: menu?.link1 }, menu?.link1Title, 1),
        createMenuItem({ dropdown: menu?.link2DropdownCollection?.items, single: menu?.link2 }, menu?.link2Title, 2),
        createMenuItem({ dropdown: menu?.link3DropdownCollection?.items, single: menu?.link3 }, menu?.link3Title, 3),
        createMenuItem({ dropdown: menu?.link4DropdownCollection?.items, single: menu?.link4 }, menu?.link4Title, 4),
    ];

    return (
        <>
            <div className={isOpen ? 'box-shadow-open h-auto md:bg-white fixed w-full z-50' : ''}>
                <div className='layout-container'>
                    <header className='relative px-10 sm:px-5 md:px-10 pt-30 md:pt-60 flex items-baseline justify-between type-style-7 font-semibold'>
                        <a
                            className='block'
                            href={formatURI('/')}>
                            <HeaderLogo bgStyle={bgStyle} />
                        </a>

                        <div className={`md:hidden flex-grow ${textColorClass} text-right`}>
                            <MobileNavigation menu={menuItems} />
                        </div>

                        <nav className={`navigation hidden md:block w-full text-center self-center ${textColorClass}${isOpen ? ' navigation--is-open' : ''}`}>
                            <ul className='navigation__margin flex justify-between items-center'>
                                {menuItems?.map((item, idx) => (
                                    <HeaderLink
                                        index={item.index}
                                        isOpen={isOpen}
                                        key={idx}
                                        linkItem={item}
                                        setIsOpen={setIsOpen} />
                                ))}
                            </ul>
                        </nav>

                        <div className='hidden md:block'>
                            <a
                                className={`block button ${buttonClass}`}
                                href='/contact'>
                                <span>{t('Get In Touch')}</span>
                                <span className='button__icon button__icon--animate'>
                                    <IconChevronSprite />
                                </span>
                            </a>
                        </div>
                    </header>
                </div>

                {menuItems?.map((link, i) => {
                    if (link?.dropdown) {
                        return (
                            <HeaderDrawer
                                isOpen={isOpen === i + 1}
                                key={i}
                                links={link?.collection}
                                setIsOpen={setIsOpen}/>
                        );
                    }
                    return null;
                })?.filter(Boolean)}
            </div>

            {/* Spacer */}
            <div className={isOpen ? 'h-[105px] block' : 'hidden'} />
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    bgStyle: PropTypes.oneOf(['transparent', 'transparent-landing', 'white']).isRequired,
    isOpen: PropTypes.bool,
    menu: PropTypes.shape({
        link1: PropTypes.object,
        link1DropdownCollection: PropTypes.object,
        link1Title: PropTypes.string,
        link2: PropTypes.object,
        link2DropdownCollection: PropTypes.object,
        link2Title: PropTypes.string,
        link3: PropTypes.object,
        link3DropdownCollection: PropTypes.object,
        link3Title: PropTypes.string,
        link4: PropTypes.object,
        link4DropdownCollection: PropTypes.object,
        link4Title: PropTypes.string,
    }),
    setIsOpen: PropTypes.func
};

Header.propTypes = propTypes;

export default Header;
